import type { AxiosRequestConfig, AxiosResponse } from "axios";
import Axios, { AxiosError } from "axios";
import { Response401Exception } from "./exceptions";
import { logout } from "@/services/user/auth/login";

interface RetryConfig extends AxiosRequestConfig {
  retryCount?: number;
}

// Array of request sending fail reasons for which we attempt to resend an API request.
const refetchableCodes = [AxiosError.ERR_NETWORK, AxiosError.ETIMEDOUT, AxiosError.ECONNABORTED];

// Array of HTTP statuses for which for which we attempt to resend an API request.
const refetchableHttpStatuses = [502, 504];

// Array of HTTP methods for which we attempt to resend an API request.
const refetchableHttpMethods = ["GET"];

// How many times should we attempt to send request again after fail?
// Keep in mind in the end the request can be sent (RefetchMaxAttempts + 1) times. Original request + RefetchMaxAttempts...
const RefetchMaxAttempts = 2;

// Try another request attempt after x miliseconds.
const RefetchDelay = 1000;

const axios = Axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
});

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (response: AxiosError) => {
    if (is401error(response)) {
      return handle401error();
    }

    if (isRefetchableResponse(response)) {
      return handleRefetchableResponseError(response);
    }

    return Promise.reject(response);
  }
);

/** For given axios error - should we try to send request again? */
function isRefetchableResponse(errorResponse: AxiosError): boolean {
  const method = getErrorResponseMethod(errorResponse);
  if (!refetchableHttpMethods.includes(method)) {
    return false;
  }

  const code = errorResponse.code;
  if (code && refetchableCodes.includes(code)) {
    return true;
  }

  const status = getErrorResponseStatus(errorResponse);
  if (status && refetchableHttpStatuses.includes(status)) {
    return true;
  }

  return false;
}

function is401error(errorResponse: AxiosError): boolean {
  const status = getErrorResponseStatus(errorResponse);
  return status === 401;
}

function handle401error(): Promise<AxiosError> {
  logout(false);
  throw new Response401Exception();
}

function handleRefetchableResponseError(errorResponse: AxiosError): Promise<AxiosResponse> {
  const config: RetryConfig = errorResponse.config as RetryConfig;
  if (!config || !config.retryCount) {
    config.retryCount = 0;
  }

  if (config.retryCount < RefetchMaxAttempts) {
    config.retryCount++;

    return new Promise<AxiosResponse>((resolve) => {
      setTimeout(() => resolve(axios(config)), RefetchDelay);
    });
  } else {
    return Promise.reject(errorResponse);
  }
}

function getErrorResponseStatus(errorResponse: AxiosError): number | undefined {
  return errorResponse.response?.status;
}

function getErrorResponseMethod(errorResponse: AxiosError): string {
  return errorResponse.request.method;
}

export { axios };
